/**
 * Guest Name Generator
 * Generates human-friendly unique names for guest participants
 * Format: [Adjective] [Noun] [UniqueNumber]
 * Examples: "Happy Penguin 42", "Brave Dragon 17"
 */

const adjectives = [
    'Happy', 'Brave', 'Clever', 'Bright', 'Swift',
    'Wise', 'Kind', 'Bold', 'Cheerful', 'Gentle',
    'Lively', 'Calm', 'Eager', 'Friendly', 'Noble',
    'Quiet', 'Jolly', 'Keen', 'Loyal', 'Mighty',
    'Proud', 'Quick', 'Serene', 'Warm', 'Witty',
    'Cosmic', 'Electric', 'Stellar', 'Radiant', 'Vibrant',
    'Cool', 'Smart', 'Creative', 'Dynamic', 'Elegant',
    'Fantastic', 'Groovy', 'Incredible', 'Jazzy', 'Lucky',
    'Marvelous', 'Nimble', 'Outstanding', 'Perfect', 'Rainbow',
    'Shiny', 'Tremendous', 'Ultimate', 'Vivid', 'Zesty'
];

const nouns = [
    'Penguin', 'Dragon', 'Phoenix', 'Tiger', 'Eagle',
    'Dolphin', 'Panda', 'Lion', 'Falcon', 'Wolf',
    'Bear', 'Hawk', 'Otter', 'Fox', 'Owl',
    'Leopard', 'Raven', 'Shark', 'Whale', 'Lynx',
    'Panther', 'Jaguar', 'Coyote', 'Badger', 'Raccoon',
    'Star', 'Comet', 'Galaxy', 'Nebula', 'Meteor',
    'Thunder', 'Lightning', 'Aurora', 'Blizzard', 'Hurricane',
    'Tornado', 'Tsunami', 'Volcano', 'Avalanche', 'Cyclone',
    'Rocket', 'Ninja', 'Wizard', 'Knight', 'Samurai',
    'Pirate', 'Explorer', 'Voyager', 'Pioneer', 'Champion'
];

/**
 * Get a random item from an array
 */
const getRandomItem = (array) => {
    return array[Math.floor(Math.random() * array.length)];
};

/**
 * Generate a unique number suffix
 * Uses timestamp-based approach for better uniqueness
 */
const generateUniqueSuffix = () => {
    // Use last 2 digits of timestamp + random 2 digits for better distribution
    const timestamp = Date.now() % 100; // Last 2 digits of timestamp
    const random = Math.floor(Math.random() * 100); // Random 0-99
    return (timestamp + random) % 100; // Combine and keep in 0-99 range
};

/**
 * Generate a human-friendly guest name
 * @param {boolean} includeNumber - Whether to include unique number suffix (default: true)
 * @returns {string} Generated guest name
 *
 * @example
 * generateGuestName() // "Happy Penguin 42"
 * generateGuestName(false) // "Brave Dragon"
 */
export const generateGuestName = (includeNumber = true) => {
    const adjective = getRandomItem(adjectives);
    const noun = getRandomItem(nouns);

    if (includeNumber) {
        const number = generateUniqueSuffix();
        return `${adjective} ${noun} ${number}`;
    }

    return `${adjective} ${noun}`;
};

/**
 * Generate multiple unique guest names
 * Ensures no duplicates in the returned set
 * @param {number} count - Number of names to generate
 * @returns {string[]} Array of unique guest names
 */
export const generateMultipleGuestNames = (count) => {
    const names = new Set();
    let attempts = 0;
    const maxAttempts = count * 10; // Prevent infinite loop

    while (names.size < count && attempts < maxAttempts) {
        names.add(generateGuestName());
        attempts++;
    }

    return Array.from(names);
};

/**
 * Check if a name appears to be a generated guest name
 * @param {string} name - Name to check
 * @returns {boolean} True if name matches guest name pattern
 */
export const isGeneratedGuestName = (name) => {
    if (!name) return false;

    // Pattern: "Word Word Number" or "Word Word"
    const pattern = /^[A-Z][a-z]+ [A-Z][a-z]+( \d+)?$/;
    return pattern.test(name);
};

export default generateGuestName;
